import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { orderBy as sortBy } from 'lodash';

import TableCell from '@mui/material/TableCell';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import FilterIcon from '@mui/icons-material/FilterList';
import ArrowUpIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownIcon from '@mui/icons-material/ArrowDownward';

import { styled } from '@mui/material/styles';

import DatePickerModal from './DatePickerModal';

import './table.scss';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: 14,
  fontWeight: 600,
  color: theme.palette.text.secondary,
  background: theme.palette.mode === 'dark' ? 'var(--color-surface-elevated)' : '#fafafa',
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: '12px 16px',
}));

const HeaderCell = (props) => {
  const {
    title,
    fieldName,
    filterOptions,
    selectedValues,
    onFilterChange,
    isDate,
    accessField,
    sort,
    order,
    orderBy,
    onSort,
    dateFilterTitle,
    dateFilterFromLabel,
    dateFilterToLabel,
  } = props;

  const [anchorEl, setAnchorEl] = useState(null);
  const [isOpen, toggleModal] = useState(false);

  const handleClickOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleSelect = (option) => {
    let values = [];

    if (selectedValues.includes(option)) {
      values = selectedValues.filter((value) => value !== option);
    } else {
      values = [...selectedValues, option];
    }

    onFilterChange(fieldName, values);
  };

  const onDateChange = (fromDate, toDate) => {
    onFilterChange(fieldName, { from: fromDate, to: toDate });
    toggleModal(false);
  };

  const hasSelectedValues = Array.isArray(selectedValues)
    ? selectedValues.length !== 0
    : !!(selectedValues?.from || selectedValues?.to);

  const handleSort = () => {
    if (sort) {
      onSort(fieldName);
    }
  };

  const renderSortIcon = () => {
    if (orderBy === fieldName) {
      return (
        <span style={{ marginTop: '2px', marginLeft: '2px' }}>
          {order === 'desc' ? (
            <ArrowUpIcon htmlColor="var(--color-text-muted)" />
          ) : (
            <ArrowDownIcon htmlColor="var(--color-text-muted)" />
          )}
        </span>
      );
    }
    return null;
  };

  return (
    <StyledTableCell>
      <div className="table-header-cell" style={{ justifyContent: accessField ? 'center' : '' }}>
        <div className="table-header-cell-title" onClick={handleSort}>
          {title}
          {sort && renderSortIcon()}
        </div>

        {filterOptions && !!filterOptions.length && (
          <div className="filter-button">
            <IconButton
              aria-controls="filter"
              aria-haspopup="true"
              size="small"
              color={hasSelectedValues ? 'primary' : 'secondary'}
              className="filter-button"
              onClick={handleClickOpenMenu}
              sx={{
                minHeight: '36px !important',
                minWidth: '36px !important',
                display: 'flex !important',
                alignItems: 'center !important',
                justifyContent: 'center !important',
              }}
            >
              <FilterIcon fontSize='small' />
            </IconButton>

            <Menu
              id="filter"
              anchorEl={anchorEl}
              keepMounted
              elevation={1}
              open={Boolean(anchorEl)}
              onClose={handleCloseMenu}
            >
              {sortBy(filterOptions).map((option) => (
                <MenuItem
                  key={option}
                  className="filter-menu-item"
                  value={option}
                  onClick={() => handleSelect(option)}
                >
                  <Checkbox
                    checked={selectedValues && selectedValues.indexOf(option) > -1}
                    color="primary"
                  />
                  <ListItemText primary={option} />
                </MenuItem>
              ))}
            </Menu>
          </div>
        )}

        {isDate && (
          <>
            <div className="filter-button">
              <IconButton
                aria-controls="filter"
                aria-haspopup="true"
                size="small"
                color={hasSelectedValues ? 'primary' : 'secondary'}
                className="filter-button"
                onClick={() => toggleModal(true)}
              >
                <FilterIcon />
              </IconButton>
            </div>

            <DatePickerModal
              open={isOpen}
              onClose={() => toggleModal(false)}
              onSubmit={onDateChange}
              title={dateFilterTitle}
              fromLabel={dateFilterFromLabel}
              toLabel={dateFilterToLabel}
              initialFromDate={selectedValues?.from || null}
              initialToDate={selectedValues?.to || null}
            />
          </>
        )}
      </div>
    </StyledTableCell>
  );
};

HeaderCell.propTypes = {
  title: PropTypes.string,
  fieldName: PropTypes.string,
  filterOptions: PropTypes.array,
  selectedValues: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  onFilterChange: PropTypes.func,
  dateFilterTitle: PropTypes.string,
  dateFilterFromLabel: PropTypes.string,
  dateFilterToLabel: PropTypes.string,
};

export default HeaderCell;
